import { ApiError, request } from './client.ts';

export interface ProgramMemoryCitation {
  label: string;
  ref: string;
}

export interface ProgramMemoryClaim {
  id: string;
  section: string;
  text: string;
  citations: ProgramMemoryCitation[];
  asOf: string;
  confidence?: number;
}

export interface ProgramMemoryClaimEnvelope {
  claims: ProgramMemoryClaim[];
  _lockedConflicts?: string[];
  _contradictions?: Array<Record<string, unknown>>;
}

export type ProgramMemoryClaims = ProgramMemoryClaim[] | ProgramMemoryClaimEnvelope | null;

export interface ProgramMemoryLatest {
  version: number;
  body_md: string;
  claims: ProgramMemoryClaims;
  created_at: string;
  status: 'published';
  id?: number;
  client_slug?: string;
  held_reason?: string | null;
  wake_id?: number | null;
}

export interface ProgramMemoryVersion {
  version: number;
  status: 'published' | 'quarantined';
  heldReason: string | null;
  createdAt: string;
  wakeId: number | null;
}

export interface ProgramMemoryLock {
  clientSlug?: string;
  section: string;
  lockedBy: string;
  lockedAt: string;
}

export interface ProgramMemoryHeldUpdate {
  version: number;
  heldReason: string;
  createdAt: string;
}

export interface ProgramMemoryResponse {
  latest: ProgramMemoryLatest | null;
  versions: ProgramMemoryVersion[];
  locks: ProgramMemoryLock[];
  lockedStale: string[];
  heldUpdate: ProgramMemoryHeldUpdate | null;
}

export interface ProgramMemoryDiff {
  claimsAdded: ProgramMemoryClaim[];
  claimsRemoved: ProgramMemoryClaim[];
  claimsChanged: ProgramMemoryClaim[];
  sectionsChanged: string[];
}

interface RawProgramMemoryLock {
  client_slug?: string;
  clientSlug?: string;
  section: string;
  locked_by?: string;
  lockedBy?: string;
  locked_at?: string;
  lockedAt?: string;
}

interface RawProgramMemoryResponse extends Omit<ProgramMemoryResponse, 'locks'> {
  locks?: RawProgramMemoryLock[];
}

function normalizeLock(lock: RawProgramMemoryLock): ProgramMemoryLock {
  return {
    clientSlug: lock.clientSlug ?? lock.client_slug,
    section: lock.section,
    lockedBy: lock.lockedBy ?? lock.locked_by ?? 'unknown',
    lockedAt: lock.lockedAt ?? lock.locked_at ?? '',
  };
}

/** Hub-side twin of tools/ws_memory.py's _claim_items. */
export function claimItems(claims: unknown): ProgramMemoryClaim[] {
  if (Array.isArray(claims)) return claims as ProgramMemoryClaim[];
  if (claims && typeof claims === 'object') {
    const items = (claims as { claims?: unknown }).claims;
    if (Array.isArray(items)) return items as ProgramMemoryClaim[];
  }
  return [];
}

export function getProgramMemory(slug: string): Promise<ProgramMemoryResponse> {
  return request<RawProgramMemoryResponse>(
    `/clients/${encodeURIComponent(slug)}/program-memory`,
  ).then((response) => ({
    latest: response.latest ?? null,
    versions: response.versions || [],
    locks: (response.locks || []).map(normalizeLock),
    lockedStale: response.lockedStale || [],
    heldUpdate: response.heldUpdate ?? null,
  }));
}

export function getProgramMemoryDiff(
  slug: string,
  fromVersion: number,
  toVersion: number,
): Promise<ProgramMemoryDiff | null> {
  return request<ProgramMemoryDiff>(
    `/clients/${encodeURIComponent(slug)}/program-memory/diff?v_from=${fromVersion}&v_to=${toVersion}`,
  ).catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  });
}

export function postLock(slug: string, section: string): Promise<ProgramMemoryLock> {
  return request<RawProgramMemoryLock>(
    `/clients/${encodeURIComponent(slug)}/program-memory/locks`,
    { method: 'POST', body: JSON.stringify({ section }) },
  ).then(normalizeLock);
}

export function deleteLock(slug: string, section: string): Promise<void> {
  return request<void>(
    `/clients/${encodeURIComponent(slug)}/program-memory/locks/${encodeURIComponent(section)}`,
    { method: 'DELETE' },
  );
}
